import { useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import SiteLayout from "@/components/feature/SiteLayout";
import Breadcrumb from "@/components/base/Breadcrumb";
import MovieCard from "@/components/base/MovieCard";
import FilterPanel from "@/pages/category/components/FilterPanel";
import { movies, genreOptions } from "@/mocks/movies";
import { actors } from "@/mocks/actors";
import { useSeo } from "@/hooks/useSeo";

const PAGE_SIZE = 6;

const sortOptions = [
  { value: "popular", label: "最受欢迎" },
  { value: "rating", label: "评分最高" },
  { value: "year-desc", label: "时间最新" },
  { value: "year-asc", label: "时间最早" },
];

export default function Category() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [filterOpen, setFilterOpen] = useState(false);

  const keyword = searchParams.get("q") || "";
  const tag = searchParams.get("tag") || "";
  const year = searchParams.get("year") || "all";
  const rating = searchParams.get("rating") || "all";
  const genre = searchParams.get("genre") || "all";
  const sort = searchParams.get("sort") || "popular";
  const currentPage = Math.max(1, Number(searchParams.get("page") || "1"));

  useSeo({
    title: "蜜桃成熟时电影分类 - 电影大全及筛选",
    description:
      "浏览蜜桃成熟时系列全部电影，按上映年份、评分等级与类型标签筛选，支持评分、时间与热度排序，快速定位想看的作品。",
    keywords: "蜜桃成熟时电影分类,电影大全,系列电影,电影筛选,电影排序",
  });

  const updateParam = (key: string, value: string) => {
    const next = new URLSearchParams(searchParams);
    if (!value || value === "all") {
      next.delete(key);
    } else {
      next.set(key, value);
    }
    if (key !== "page") {
      next.delete("page");
    }
    setSearchParams(next);
  };

  const resetAll = () => setSearchParams({});

  const filteredMovies = useMemo(() => {
    const query = keyword.trim().toLowerCase();
    const matchedActorIds = query
      ? actors
          .filter(
            (actor) =>
              actor.name.toLowerCase().includes(query) || actor.englishName.toLowerCase().includes(query),
          )
          .map((actor) => actor.id)
      : [];

    const result = movies.filter((movie) => {
      if (tag && !movie.tags.includes(tag)) return false;
      if (year !== "all" && String(Math.floor(movie.year / 10) * 10) !== year) return false;
      if (rating !== "all" && movie.rating < Number(rating)) return false;
      if (genre !== "all" && !movie.genres.includes(genre)) return false;
      if (query) {
        const hitMovie =
          movie.title.toLowerCase().includes(query) ||
          movie.englishTitle.toLowerCase().includes(query) ||
          movie.director.toLowerCase().includes(query) ||
          movie.summary.toLowerCase().includes(query) ||
          movie.genres.some((item) => item.toLowerCase().includes(query)) ||
          movie.tags.some((item) => item.toLowerCase().includes(query));
        const hitActor = movie.cast.some((id) => matchedActorIds.includes(id));
        if (!hitMovie && !hitActor) return false;
      }
      return true;
    });

    return result.sort((a, b) => {
      if (sort === "rating") return b.rating - a.rating;
      if (sort === "year-desc") return b.year - a.year;
      if (sort === "year-asc") return a.year - b.year;
      return b.popularity - a.popularity;
    });
  }, [keyword, tag, year, rating, genre, sort]);

  const totalPages = Math.max(1, Math.ceil(filteredMovies.length / PAGE_SIZE));
  const safePage = Math.min(currentPage, totalPages);
  const pageMovies = filteredMovies.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE);

  const activeFilterCount =
    (year !== "all" ? 1 : 0) + (rating !== "all" ? 1 : 0) + (genre !== "all" ? 1 : 0) + (tag ? 1 : 0);

  const filterProps = {
    year,
    rating,
    genre,
    genreOptions,
    onYearChange: (value: string) => updateParam("year", value),
    onRatingChange: (value: string) => updateParam("rating", value),
    onGenreChange: (value: string) => updateParam("genre", value),
    onReset: resetAll,
  };

  return (
    <SiteLayout>
      <div className="border-b border-background-200 bg-background-100">
        <div className="mx-auto w-full max-w-[1240px] px-4 py-7 md:px-6 md:py-10">
          <Breadcrumb items={[{ label: "首页", to: "/" }, { label: "电影分类" }]} />
          <div className="mt-4 flex flex-col gap-3 md:flex-row md:items-end md:justify-between">
            <div>
              <h1 className="font-heading text-2xl font-semibold text-foreground-950 md:text-3xl">
                {tag ? `${tag} · 电影分类` : "蜜桃成熟时电影分类"}
              </h1>
              <p className="mt-2 max-w-2xl text-sm leading-relaxed text-foreground-600">
                收录系列全部片目与纪念作品，可按上映年份、评分与类型筛选，也可以通过关键词直接定位影片或演员。
              </p>
            </div>
            <p className="text-xs text-foreground-500">
              共 <span className="font-medium text-accent-600">{filteredMovies.length}</span> 部影片
            </p>
          </div>

          {(keyword || tag) && (
            <div className="mt-4 flex flex-wrap items-center gap-2 text-xs">
              {keyword && (
                <span className="inline-flex items-center gap-2 rounded-full bg-background-50 px-3 py-1.5 text-foreground-700">
                  关键词：{keyword}
                  <button
                    type="button"
                    onClick={() => updateParam("q", "")}
                    aria-label="清除关键词"
                    className="flex h-4 w-4 items-center justify-center text-foreground-400 hover:text-accent-600"
                  >
                    <i className="ri-close-line" />
                  </button>
                </span>
              )}
              {tag && (
                <span className="inline-flex items-center gap-2 rounded-full bg-accent-100 px-3 py-1.5 text-accent-800">
                  标签：{tag}
                  <button
                    type="button"
                    onClick={() => updateParam("tag", "")}
                    aria-label="清除标签"
                    className="flex h-4 w-4 items-center justify-center hover:text-accent-950"
                  >
                    <i className="ri-close-line" />
                  </button>
                </span>
              )}
            </div>
          )}
        </div>
      </div>

      <div className="mx-auto w-full max-w-[1240px] px-4 py-8 md:px-6 md:py-12">
        <div className="flex flex-col gap-8 lg:flex-row lg:gap-10">
          <aside className="hidden w-[236px] shrink-0 lg:block">
            <div className="sticky top-[100px] rounded-lg border border-background-200/80 bg-background-50 p-5">
              <div className="mb-5 flex items-center justify-between">
                <h2 className="font-heading text-sm font-semibold text-foreground-950">筛选条件</h2>
                {activeFilterCount > 0 && (
                  <span className="rounded-full bg-accent-100 px-2 py-0.5 text-[10px] text-accent-700">
                    {activeFilterCount} 项
                  </span>
                )}
              </div>
              <FilterPanel {...filterProps} />
            </div>
          </aside>

          <div className="min-w-0 flex-1">
            <div className="mb-5 flex flex-wrap items-center justify-between gap-3">
              <button
                type="button"
                onClick={() => setFilterOpen(true)}
                className="inline-flex items-center gap-2 whitespace-nowrap rounded-md border border-background-300 bg-background-50 px-3.5 py-2 text-xs text-foreground-700 lg:hidden"
              >
                <i className="ri-filter-3-line" />
                筛选{activeFilterCount > 0 ? `（${activeFilterCount}）` : ""}
              </button>
              <div className="flex items-center gap-2 text-xs text-foreground-500">
                <span className="hidden sm:inline">排序</span>
                <div className="flex flex-wrap items-center gap-1.5">
                  {sortOptions.map((option) => (
                    <button
                      key={option.value}
                      type="button"
                      onClick={() => updateParam("sort", option.value)}
                      className={`whitespace-nowrap rounded-md px-2.5 py-1.5 text-xs transition-colors ${
                        sort === option.value
                          ? "bg-foreground-900 text-background-50"
                          : "bg-background-100 text-foreground-600 hover:text-accent-600"
                      }`}
                    >
                      {option.label}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {pageMovies.length > 0 ? (
              <>
                <div className="flex flex-col gap-3 sm:hidden">
                  {pageMovies.map((movie) => (
                    <Link
                      key={movie.id}
                      to={`/detail/${movie.id}`}
                      className="flex gap-3 rounded-lg border border-background-200/80 bg-background-50 p-3"
                    >
                      <span className="h-[112px] w-[80px] shrink-0 overflow-hidden rounded-md bg-background-200">
                        <img
                          src={movie.cover}
                          alt={`${movie.title}电影封面`}
                          title={`${movie.title}剧情介绍与演员资料`}
                          className="h-full w-full object-cover object-top"
                        />
                      </span>
                      <span className="flex min-w-0 flex-1 flex-col">
                        <span className="flex items-start justify-between gap-2">
                          <span className="font-heading text-sm font-semibold text-foreground-950">
                            {movie.title}
                          </span>
                          <span className="flex shrink-0 items-center gap-1 text-[11px] text-accent-600">
                            <i className="ri-star-fill text-[9px]" />
                            {movie.rating.toFixed(1)}
                          </span>
                        </span>
                        <span className="mt-1 text-[11px] text-foreground-400">
                          {movie.year} · {movie.genres.join(" / ")} · {movie.duration} 分钟
                        </span>
                        <span className="mt-1.5 line-clamp-2 text-xs leading-relaxed text-foreground-500">
                          {movie.summary}
                        </span>
                        <span className="mt-auto flex items-center justify-between pt-1.5 text-[11px] text-foreground-500">
                          <span>导演 {movie.director}</span>
                          <span className="flex items-center gap-1 text-accent-600">
                            查看详情
                            <i className="ri-arrow-right-s-line" />
                          </span>
                        </span>
                      </span>
                    </Link>
                  ))}
                </div>

                <div className="hidden gap-4 sm:grid sm:grid-cols-2 md:grid-cols-3 md:gap-5">
                  {pageMovies.map((movie) => (
                    <MovieCard key={movie.id} movie={movie} />
                  ))}
                </div>

                <div className="mt-8 flex flex-wrap items-center justify-center gap-2">
                  <button
                    type="button"
                    disabled={safePage <= 1}
                    onClick={() => updateParam("page", String(safePage - 1))}
                    className="inline-flex items-center gap-1 whitespace-nowrap rounded-md border border-background-300 px-3 py-2 text-xs text-foreground-600 transition-colors hover:border-primary-400 disabled:cursor-not-allowed disabled:opacity-40"
                  >
                    <i className="ri-arrow-left-s-line" />
                    上一页
                  </button>
                  {Array.from({ length: totalPages }).map((_, index) => (
                    <button
                      key={index}
                      type="button"
                      onClick={() => updateParam("page", String(index + 1))}
                      className={`h-8 w-8 rounded-md text-xs transition-colors ${
                        safePage === index + 1
                          ? "bg-accent-500 text-background-50"
                          : "border border-background-300 text-foreground-600 hover:border-primary-400"
                      }`}
                    >
                      {index + 1}
                    </button>
                  ))}
                  <button
                    type="button"
                    disabled={safePage >= totalPages}
                    onClick={() => updateParam("page", String(safePage + 1))}
                    className="inline-flex items-center gap-1 whitespace-nowrap rounded-md border border-background-300 px-3 py-2 text-xs text-foreground-600 transition-colors hover:border-primary-400 disabled:cursor-not-allowed disabled:opacity-40"
                  >
                    下一页
                    <i className="ri-arrow-right-s-line" />
                  </button>
                </div>
              </>
            ) : (
              <div className="flex flex-col items-center gap-3 rounded-lg border border-dashed border-background-300 bg-background-100 px-6 py-16 text-center">
                <span className="flex h-12 w-12 items-center justify-center rounded-full bg-background-200 text-foreground-400">
                  <i className="ri-search-eye-line text-xl" />
                </span>
                <h3 className="font-heading text-base font-semibold text-foreground-900">没有符合条件的影片</h3>
                <p className="max-w-sm text-xs leading-relaxed text-foreground-500">
                  试着放宽年份或评分条件，或者清除关键词后重新浏览系列片目。
                </p>
                <div className="mt-2 flex flex-wrap items-center justify-center gap-2">
                  <button
                    type="button"
                    onClick={resetAll}
                    className="whitespace-nowrap rounded-md bg-primary-500 px-4 py-2 text-xs font-medium text-background-50 transition-colors hover:bg-primary-600"
                  >
                    重置筛选
                  </button>
                  <Link
                    to="/category"
                    className="whitespace-nowrap rounded-md border border-background-300 px-4 py-2 text-xs text-foreground-600 transition-colors hover:border-primary-400"
                  >
                    返回全部分类
                  </Link>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {filterOpen && (
        <div className="fixed inset-0 z-[60] lg:hidden">
          <button
            type="button"
            aria-label="关闭筛选"
            onClick={() => setFilterOpen(false)}
            className="absolute inset-0 bg-foreground-950/50"
          />
          <div className="absolute inset-x-0 bottom-0 max-h-[85vh] overflow-y-auto rounded-t-2xl bg-background-50 p-5">
            <div className="mb-5 flex items-center justify-between">
              <h2 className="font-heading text-base font-semibold text-foreground-950">筛选条件</h2>
              <button
                type="button"
                onClick={() => setFilterOpen(false)}
                aria-label="关闭"
                className="flex h-8 w-8 items-center justify-center rounded-md border border-background-300 text-foreground-600"
              >
                <i className="ri-close-line" />
              </button>
            </div>
            <FilterPanel {...filterProps} />
            <button
              type="button"
              onClick={() => setFilterOpen(false)}
              className="mt-6 w-full whitespace-nowrap rounded-md bg-primary-500 py-3 text-sm font-medium text-background-50"
            >
              查看 {filteredMovies.length} 部影片
            </button>
          </div>
        </div>
      )}
    </SiteLayout>
  );
}